import * as React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/cn';
import { LAUNCHED_STATES, stateSlugToDisplay, buildStateHubUrl } from '../../lib/stateCore.mjs';
import type { StateSlug } from '../../lib/stateCore.mjs';

/**
 * StateHubNav — sibling navigation between the suburb index hubs (/nsw/suburbs ↔ /vic/suburbs since UI-57).
 *
 * Semantics mirror the blog's CategoryPillNav deliberately: this is REAL NAVIGATION —
 * each item is a react-router <Link> to a different route — NOT a tablist. So the ARIA
 * is a <nav> with an aria-label plus aria-current="page" on the active link, and there
 * are no role="tab"/"tablist" attributes. Sibling <Link>s survive into the prerendered
 * HTML (verified on the shipped dist/blog/index.html), so these are crawlable internal
 * links, which is the point of using links rather than a <select>.
 *
 * ⚠️ ITEMS DERIVE FROM LAUNCHED_STATES, NEVER FROM STATE_SLUGS. A control offering an
 * unlaunched state would emit a crawlable internal link to a soft-404: nothing is built
 * for an unlaunched state, and firebase.json's "/vic/**" rewrite serves /notfound.html
 * only because no static file outranks it. Binding to LAUNCHED_STATES makes that
 * structurally impossible rather than merely avoided. lib/stateHubNav.test.ts locks it.
 *
 * ⚠️ RENDERS NOTHING BELOW TWO LAUNCHED STATES. With LAUNCHED_STATES === ['nsw'] there is
 * nothing to switch to, and a one-item nav is furniture: it cannot be used, and a <nav>
 * whose only child is the page you are already on is noise for a screen reader. The hub
 * is therefore byte-unchanged today, and this appears on its own at the flip.
 *
 * Hub-only by design. From a SUBURB page there is no correct destination: 582 of NSW's
 * 601 suburb slugs have no VIC counterpart, and the 19 that collide are DIFFERENT PLACES
 * (Sydney's Newtown 2042 vs Geelong's Newtown 3220). Mapping suburb-to-suburb would look
 * like it worked and be wrong. Do not mount this on suburb, style or gym-profile pages
 * without a separate ruling; the only defensible target from those is the other state's
 * hub, which is a different control with different copy.
 */

export interface StateHubNavItem {
    state: StateSlug;
    /** Display token, e.g. 'NSW'. */
    label: string;
    /** The state's hub path, e.g. '/nsw/suburbs' (UI-57: was '/nsw/gyms'). */
    href: string;
}

/**
 * Derive the nav items for a states array (pure).
 *
 * Exported so the multi-state behaviour is testable TODAY, without flipping anything —
 * the same shape lib/stateRoutes.ts uses (`deriveStateRoutePaths(states)` pure, plus an
 * app instance bound to LAUNCHED_STATES).
 */
export function deriveStateHubNavItems(states: readonly StateSlug[]): StateHubNavItem[] {
    return states.map((state) => ({
        state,
        label: stateSlugToDisplay(state),
        href: buildStateHubUrl(state),
    }));
}

/** App instance — bound to LAUNCHED_STATES. */
export const STATE_HUB_NAV_ITEMS = deriveStateHubNavItems(LAUNCHED_STATES);

interface StateHubNavProps {
    /** The hub whose page this is. */
    activeState: StateSlug;
}

const StateHubNav: React.FC<StateHubNavProps> = ({ activeState }) => {
    // Nothing to switch between — see the docblock.
    if (STATE_HUB_NAV_ITEMS.length < 2) return null;

    return (
        <nav aria-label="State navigation" className="mb-8">
            <ul className="flex flex-wrap gap-3">
                {STATE_HUB_NAV_ITEMS.map((item) => {
                    const isActive = item.state === activeState;
                    return (
                        <li key={item.state} className="flex">
                            <Link
                                to={item.href}
                                aria-current={isActive ? 'page' : undefined}
                                className={cn(
                                    'flex items-center justify-center whitespace-nowrap px-4 h-11 rounded-md border text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary',
                                    isActive
                                        ? 'border-brand-primary text-text-primary font-semibold'
                                        : 'border-border-default text-text-muted hover:text-text-primary hover:border-text-subtle font-medium',
                                )}
                            >
                                {item.label}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

export default StateHubNav;
